import { HistoryToggleOff, Warning } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { max } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

import { ReactComponent as SupportKeyIcon } from '@work4all/assets/icons/support-key.svg';
import w4aLogo from '@work4all/assets/img/work4all.png';

import { Dialog, DialogActions, DialogContent } from '@work4all/components';
import { Divider } from '@work4all/components/lib/dataDisplay/divider/Divider';
import { NumberInput } from '@work4all/components/lib/input/number-input';

import { Navigate, useUser } from '@work4all/data';

import {
  RequestStatus,
  useServiceAccessApi,
} from './hooks/use-service-access-api';

const HELPCENTER_URL =
  'https://help.work4all.de/?id=debf3d6c-0084-4775-a774-f3cf9bddae05';

export const ServiceAccessManagementPage = () => {
  const user = useUser();

  const loc = useLocation();

  if (!user) {
    return <Navigate to={`/login`} tenant={null} state={{ from: loc }} />;
  }
  return <ServiceAccessManagementPageInner />;
};
export const ServiceAccessManagementPageInner = () => {
  const params = useSearchParams();
  const serviceAccessRequestId = params[0]?.get('id');
  const user = useUser();
  const [errorState, setErrorState] = useState(false);
  const [requestStatus, setRequestStatus] = useState<RequestStatus>(null);
  const [hoursValid, setHoursValid] = useState(24);
  const [loading, setLoading] = useState(true);

  const { t } = useTranslation();

  const userApiUrl = useMemo(() => user?.baseUrl + '/api', [user?.baseUrl]);

  const { validateExternalRequest, grantExternalRequest, denyExternalRequest } =
    useServiceAccessApi();

  const [serviceAccessRequestValid, setServiceAccessRequestValid] =
    useState<boolean>(false);

  const refreshServiceAccessRequest = useCallback(() => {
    if (!serviceAccessRequestId) return;
    validateExternalRequest(serviceAccessRequestId, userApiUrl).then(
      (request) => {
        setLoading(false);
        setServiceAccessRequestValid(request);
        if (request) {
          setRequestStatus(RequestStatus.OPEN);
        }
      }
    );
  }, [validateExternalRequest, serviceAccessRequestId, userApiUrl]);

  useEffect(() => {
    refreshServiceAccessRequest();
  }, [refreshServiceAccessRequest]);

  const handleGrant = useCallback(() => {
    setLoading(true);
    grantExternalRequest(serviceAccessRequestId, hoursValid, userApiUrl)
      .then(() => {
        setRequestStatus(RequestStatus.GRANTED);
        setLoading(false);
      })
      .catch(() => {
        setErrorState(true);
        setLoading(false);
      });
  }, [grantExternalRequest, serviceAccessRequestId, hoursValid, userApiUrl]);

  const handleDeny = useCallback(() => {
    denyExternalRequest(serviceAccessRequestId, userApiUrl)
      .then(() => {
        setRequestStatus(RequestStatus.DENIED);
      })
      .catch(() => {
        setErrorState(true);
        setLoading(false);
      });
  }, [denyExternalRequest, serviceAccessRequestId, userApiUrl]);

  if (errorState) {
    return (
      <Box
        width="100%"
        height="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
        gap="0.5rem"
      >
        <Warning color="error" />
        <Typography variant="body1" align="center">
          {t('SERVICE_ACCESS.ERROR')}
        </Typography>
      </Box>
    );
  }

  if (loading && !serviceAccessRequestValid) {
    return (
      <Box
        width="100%"
        height="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
        gap="0.5rem"
      >
        <CircularProgress variant="indeterminate" />
        <Typography variant="body1" align="center">
          {t('COMMON.LOADING')}
        </Typography>
      </Box>
    );
  }

  if (!loading && !serviceAccessRequestValid) {
    return (
      <Box
        gap="0.5rem"
        width="100%"
        height="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <HistoryToggleOff color="error" fontSize="large" />
        <Typography variant="body1" align="center">
          {t('SERVICE_ACCESS.INVALID')}
        </Typography>
      </Box>
    );
  }

  return (
    <Dialog open={true} closeButton={false}>
      {requestStatus === RequestStatus.OPEN ? (
        <DialogContent>
          <Stack alignItems="center" gap="1rem" maxWidth="20rem">
            <SupportKeyIcon width={100} height={100} color="var(--brand01)" />
            <Typography variant="h3">{t('SERVICE_ACCESS.TITLE')}</Typography>
            <Typography variant="body1" align="center">
              {t('SERVICE_ACCESS.DESCRIPTION')}
            </Typography>
            <NumberInput
              value={hoursValid}
              onChange={(e) => setHoursValid(max([1, e]))}
              unit="h"
              min={1}
              style={{ width: '4rem' }}
              label={t('COMMON.VALID')}
              disabled={loading}
              selectOnFocus
              autoFocus
            />
          </Stack>
        </DialogContent>
      ) : requestStatus === RequestStatus.GRANTED ? (
        <DialogContent>
          <Stack alignItems="center" gap="1rem" maxWidth="20rem">
            <SupportKeyIcon width={100} height={100} color="var(--success)" />
            <Typography variant="h3">{t('SERVICE_ACCESS.TITLE')}</Typography>
            <Typography variant="body1" align="center">
              {t('SERVICE_ACCESS.GRANTED_DESCRIPTION')}
            </Typography>
          </Stack>
        </DialogContent>
      ) : (
        <DialogContent>
          <Stack alignItems="center" gap="1rem" maxWidth="20rem">
            <SupportKeyIcon width={100} height={100} color="var(--error)" />
            <Typography variant="h3">{t('SERVICE_ACCESS.TITLE')}</Typography>
            <Typography variant="body1" align="center">
              {t('SERVICE_ACCESS.DENIED_DESCRIPTION')}
            </Typography>
            <Typography variant="body1" align="center">
              <Link to={HELPCENTER_URL} target="_blank">
                {t('SERVICE_ACCESS.READ_MORE')}
              </Link>
            </Typography>
          </Stack>
        </DialogContent>
      )}
      {requestStatus === RequestStatus.OPEN && (
        <DialogActions>
          <Button color="error" onClick={handleDeny} disabled={loading}>
            {t('SERVICE_ACCESS.DENY')}
          </Button>
          <Button color="primary" onClick={handleGrant} disabled={loading}>
            {t('SERVICE_ACCESS.GRANT')}
          </Button>
        </DialogActions>
      )}
      <Stack alignItems="center" pb="1rem" gap="1rem">
        <Divider style={{ width: '100%', padding: '0' }} />
        <Stack direction="row" gap="0.5rem" alignItems="center">
          <img src={w4aLogo} alt="work4all Logo" height="16rem" />
          <Typography variant="caption" align="center" color="var(--text03)">
            work4all GmbH
          </Typography>
        </Stack>
      </Stack>
    </Dialog>
  );
};
